<template>
	<v-row v-if="!customerView" class="full-height customer-page">
		<!-- Tab Navigation Header -->
		<v-col cols="12" class="flex-none pb-0">
			<v-card flat class="customer-tab-card">
				<v-card-text class="pa-3">
					<div class="customer-header-tabs">
						<v-btn
							depressed
							class="mr-2"
							:class="activeTab === 1 ? 'active' : null"
							@click="switchToCustomerTab"
						>
							<v-icon>mdi-account-box</v-icon> Customer
						</v-btn>
						<v-btn
							depressed
							:class="activeTab === 2 ? 'active' : null"
							@click="switchToContactTab"
						>
							<v-icon>mdi-account-search</v-icon> Contact
						</v-btn>
					</div>
				</v-card-text>
			</v-card>
		</v-col>

		<!-- Customer Table -->
		<v-col v-if="activeTab === 1" cols="12" class="flex-auto pt-0 customer-table-container">
			<v-card flat class="customer-toolbar-card">
				<v-card-text class="pa-3">
					<v-row align="center" dense>
						<v-col cols="12" sm="6" md="4">
							<v-text-field
								v-model="searchForm.keywords"
								placeholder="Search customers..."
								prepend-inner-icon="mdi-magnify"
								outlined
								dense
								hide-details
								clearable
								class="customer-search"
								@input="onCustomerSearchInput"
								@click:clear="onCustomerSearchClear"
							></v-text-field>
						</v-col>
						<v-spacer></v-spacer>
						<v-col cols="auto">
							<v-btn depressed rounded color="success" @click="$router.push('/relationships/customer/new')">
								New Customer
							</v-btn>
						</v-col>
					</v-row>
				</v-card-text>
			</v-card>

			<v-data-table
				:headers="customerHeaders"
				:items="customers"
				:loading="$apollo.queries.customers.loading"
				:options.sync="customerTableOptions"
				:server-items-length="customerTotalCount"
				:footer-props="footerProps"
				:mobile-breakpoint="0"
				class="customer-data-table"
				@click:row="getCustomer"
				must-sort
				fixed-header
			>
				<!-- Organization Column -->
				<template v-slot:item.organization="{ item }">
					{{ item.organization }}
				</template>

				<!-- Address Column -->
				<template v-slot:item.address="{ item }">
					{{ renderAddress(item) }}
				</template>

				<!-- Rep Column -->
				<template v-slot:item.salesRep="{ item }">
					{{ item.salesRep ? item.salesRep.name : '' }}
				</template>

				<!-- Customer Number Column -->
				<template v-slot:item.customerNumber="{ item }">
					{{ item.customerNumber }}
				</template>

				<!-- Loading slot -->
				<template v-slot:loading>
					<v-skeleton-loader type="table-row@10"></v-skeleton-loader>
				</template>

				<!-- No data slot -->
				<template v-slot:no-data>
					<div class="text-center pa-4">
						<v-icon large color="grey lighten-1">mdi-account-group</v-icon>
						<p class="mt-2 grey--text">No customers found</p>
					</div>
				</template>
			</v-data-table>
		</v-col>

		<!-- Contact Table -->
		<v-col v-if="activeTab === 2" cols="12" class="flex-auto pt-0 customer-table-container">
			<v-card flat class="customer-toolbar-card">
				<v-card-text class="pa-3">
					<v-row align="center" dense>
						<v-col cols="12" sm="6" md="4">
							<v-text-field
								v-model="searchForm.keywords"
								placeholder="Search contacts..."
								prepend-inner-icon="mdi-magnify"
								outlined
								dense
								hide-details
								clearable
								class="customer-search"
								@input="onContactSearchInput"
								@click:clear="onContactSearchClear"
							></v-text-field>
						</v-col>
						<v-col cols="auto">
							<v-checkbox
								class="form-checkbox mt-0"
								v-model="searchForm.inactive"
								label="Show Inactive"
								color="gray"
								hide-details="auto"
								@change="onFilterChange"
							></v-checkbox>
						</v-col>
						<v-spacer></v-spacer>
						<v-col cols="auto">
							<v-btn depressed rounded color="success" @click="$router.push('/relationships/customer/new')">
								New Customer
							</v-btn>
						</v-col>
					</v-row>
				</v-card-text>
			</v-card>

			<v-data-table
				:headers="contactHeaders"
				:items="contacts"
				:loading="$apollo.queries.contacts.loading"
				:options.sync="contactTableOptions"
				:server-items-length="contactTotalCount"
				:footer-props="footerProps"
				:mobile-breakpoint="0"
				class="customer-data-table"
				@click:row="getCustomer"
				must-sort
				fixed-header
			>
				<!-- First Name Column -->
				<template v-slot:item.firstName="{ item }">
					{{ item.firstName }}
				</template>

				<!-- Last Name Column -->
				<template v-slot:item.lastName="{ item }">
					{{ item.lastName }}
				</template>

				<!-- Work Phone Column -->
				<template v-slot:item.workPhoneNumber="{ item }">
					{{ item.workPhoneNumber }}
				</template>

				<!-- Cell Phone Column -->
				<template v-slot:item.cellPhoneNumber="{ item }">
					{{ item.cellPhoneNumber }}
				</template>

				<!-- Email Column -->
				<template v-slot:item.email="{ item }">
					{{ item.email }}
				</template>

				<!-- Customer Number Column -->
				<template v-slot:item.customerNumber="{ item }">
					{{ item.customer ? item.customer.customerNumber : "" }}
				</template>

				<!-- Loading slot -->
				<template v-slot:loading>
					<v-skeleton-loader type="table-row@10"></v-skeleton-loader>
				</template>

				<!-- No data slot -->
				<template v-slot:no-data>
					<div class="text-center pa-4">
						<v-icon large color="grey lighten-1">mdi-account-search</v-icon>
						<p class="mt-2 grey--text">No contacts found</p>
					</div>
				</template>
			</v-data-table>
		</v-col>

		<MergeCustomer
			v-if="mergeCustomer"
			:showModal="mergeCustomer"
			@cancel="mergeCustomer = false"
		/>
	</v-row>
</template>

<script>
import gql from "graphql-tag";
import MergeCustomer from "@/components/Relationships/MergeCustomer.vue";

export default {
	name: "CustomerList",
	components: {
		MergeCustomer,
	},
	props: {
		activeTab: {
			type: Number,
			default: 1,
		},
		addressTab: {
			type: Boolean,
			default: false,
		},
		returnToQuote: {
			type: Boolean,
			default: false,
		},
		quoteId: {
			type: Number,
			default: null,
		},
	},
	data() {
		return {
			searchForm: {
				keywords: "",
				inactive: false,
			},
			customers: [],
			contacts: [],
			customerView: false,
			mergeCustomer: false,
			customer: {},
			customerTotalCount: 0,
			contactTotalCount: 0,
			searchTimeout: null,
			customerTableOptions: {
				page: 1,
				itemsPerPage: 15,
				sortBy: ["organization"],
				sortDesc: [false],
			},
			contactTableOptions: {
				page: 1,
				itemsPerPage: 15,
				sortBy: ["firstName"],
				sortDesc: [false],
			},
			footerProps: {
				"items-per-page-options": [10, 15, 25, 50],
				"show-first-last-page": true,
			},
			customerHeaders: [
				{
					text: "Organization",
					value: "organization",
					sortable: true,
					width: "28%",
				},
				{
					text: "Address",
					value: "address",
					sortable: false,
					width: "38%",
				},
				{
					text: "Rep",
					value: "salesRep",
					sortable: true,
					width: "18%",
				},
				{
					text: "Customer #",
					value: "customerNumber",
					align: "center", // numeric column (#1178)
					sortable: true,
					width: "16%",
				},
			],
			contactHeaders: [
				{
					text: "First Name",
					value: "firstName",
					sortable: true,
					width: "18%",
				},
				{
					text: "Last Name",
					value: "lastName",
					sortable: true,
					width: "18%",
				},
				{
					text: "Work Phone Number",
					value: "workPhoneNumber",
					sortable: false,
					width: "16%",
				},
				{
					text: "Cell Phone Number",
					value: "cellPhoneNumber",
					sortable: false,
					width: "16%",
				},
				{
					text: "Email Address",
					value: "email",
					sortable: true,
					width: "18%",
				},
				{
					text: "Customer #",
					value: "customerNumber",
					align: "center", // numeric column (#1178)
					sortable: false,
					width: "14%",
				},
			],
		};
	},
	apollo: {
		customers: {
			query() {
				return gql`
					query GetCustomers(
						$pageOffset: Int!
						$take: Int!
						$order: [CustomerSortInput!] = [{ organization: ASC }]
						$where: CustomerFilterInput = null
					) {
						customers(skip: $pageOffset, take: $take, order: $order, where: $where) {
							totalCount
							items {
								customerId
								organization
								salesRep {
									name
								}
								customerNumber
								salesTaxId
								addresses {
									addressLine1
									addressLine2
									city
									state
									postalCode
									isActive
								}
							}
						}
					}
				`;
			},
			variables() {
				return this.buildCustomerQueryVariables();
			},
			skip() {
				return this.activeTab !== 1;
			},
			update(data) {
				this.customerTotalCount = data.customers.totalCount;

				// Auto-navigate if exactly one result is found from a search with exact match
				if (
					this.searchForm.keywords.trim() !== "" &&
					data.customers.items.length === 1 &&
					(this.searchForm.keywords === data.customers.items[0]?.organization ||
						this.searchForm.keywords === data.customers.items[0]?.customerNumber)
				) {
					this.$nextTick(() => {
						this.getCustomer(data.customers.items[0]);
					});
				}

				return data.customers.items;
			},
			notifyOnNetworkStatusChange: true,
			error(error) {
				console.error("Error fetching customers:", error);
			},
		},
		contacts: {
			query() {
				return gql`
					query GetContacts(
						$pageOffset: Int!
						$take: Int!
						$order: [ContactSortInput!] = [{ firstName: ASC }]
						$where: ContactFilterInput = null
					) {
						contacts(skip: $pageOffset, take: $take, order: $order, where: $where) {
							totalCount
							items {
								contactId
								firstName
								lastName
								cellPhoneNumber
								workPhoneNumber
								email
								customerId
								customer {
									customerNumber
								}
							}
						}
					}
				`;
			},
			variables() {
				return this.buildContactQueryVariables();
			},
			skip() {
				return this.activeTab !== 2;
			},
			update(data) {
				this.contactTotalCount = data.contacts.totalCount || 0;

				// Auto-navigate if exactly one result is found from a search with exact match
				if (
					this.searchForm.keywords.trim() !== "" &&
					data.contacts.items.length === 1 &&
					(this.searchForm.keywords === data.contacts.items[0]?.email ||
						this.searchForm.keywords === `${data.contacts.items[0]?.lastName}`)
				) {
					this.$nextTick(() => {
						this.getCustomer(data.contacts.items[0]);
					});
				}

				return data.contacts.items;
			},
			notifyOnNetworkStatusChange: true,
			error(error) {
				console.error("Error fetching contacts:", error);
			},
		},
		},
	computed: {
		navigationDirection() {
			return this.$store.getters["globalState/navigationDirection"];
		},
	},
	watch: {
		customerTableOptions: {
			handler() {
				if (this.activeTab === 1) {
					this.fetchCustomerData();
				}
			},
			deep: true,
		},
		contactTableOptions: {
			handler() {
				if (this.activeTab === 2) {
					this.fetchContactData();
				}
			},
			deep: true,
		},
		navigationDirection: {
			handler(newDirection) {
				if (newDirection) {
					const savedState = this.$store.getters["globalState/customersState"];
					if (savedState) {
						this.searchForm.keywords = savedState.keywords || "";
						this.searchForm.inactive = savedState.showInactive || false;

						if (this.activeTab === 1) {
							this.customerTableOptions.page = (savedState.customerPage || 0) + 1;
							if (savedState.customerSortBy && savedState.customerSortBy.length > 0) {
								this.customerTableOptions.sortBy = savedState.customerSortBy;
								this.customerTableOptions.sortDesc = savedState.customerSortDesc || [false];
							}
						} else if (this.activeTab === 2) {
							this.contactTableOptions.page = (savedState.contactPage || 0) + 1;
							if (savedState.contactSortBy && savedState.contactSortBy.length > 0) {
								this.contactTableOptions.sortBy = savedState.contactSortBy;
								this.contactTableOptions.sortDesc = savedState.contactSortDesc || [false];
							}
						}

						this.$nextTick(() => {
							if (typeof savedState.scrollPosition === "number") {
								setTimeout(() => {
									window.scrollTo({
										top: savedState.scrollPosition,
										left: 0,
										behavior: "smooth",
									});
								}, 500);
							}
						});
					}
				}
			},
			immediate: true,
		},
	},
	methods: {
		switchToCustomerTab() {
			this.$router.push("/relationships/customer");
			this.searchForm.inactive = false;
			this.searchForm.keywords = "";
			this.customerTableOptions.page = 1;
		},

		switchToContactTab() {
			this.$router.push("/relationships/customer/contacts");
			this.searchForm.keywords = "";
			this.contactTableOptions.page = 1;
		},

		renderAddress(customer) {
			//check if addresses object exists and generate an address string
			if (customer.addresses && customer.addresses.length > 0) {
				const address = `${customer.addresses[0].addressLine1}, ${
					customer.addresses[0].addressLine2 ? customer.addresses[0].addressLine2 : ""
				} ${customer.addresses[0].city}, ${customer.addresses[0].state} ${
					customer.addresses[0].postalCode
				}`;
				return address;
			} else {
				return "";
			}
		},

		updateCustomers() {
			this.customerView = false;
			if (this.activeTab === 1) {
				this.fetchCustomerData();
			} else {
				this.fetchContactData();
			}
		},

		async getCustomer(customer, addressTab = false) {
			if (this.activeTab === 1) {
				this.$router.push(
					this.$route.query.initToNewCustomerView === "true"
						? `/relationships/customer/${customer.customerId}?addressTab=${addressTab}&initToNewCustomerView=true`
						: `/relationships/customer/${customer.customerId}?addressTab=${addressTab}`
				);
			} else {
				this.$router.push(
					`/relationships/customer/${customer.customerId}?addressTab=${addressTab}`
				);
			}
		},

		async loadCustomerFromQueryParam() {
			let customerId = this.$route.query.customerId;
			if (customerId) {
				let cust = { customerId: customerId };
				await this.getCustomer(cust, this.$route.query.addressTab === "true");
			}
		},

		onCustomerSearchInput() {
			// Debounced search
			if (this.searchTimeout) {
				clearTimeout(this.searchTimeout);
			}
			this.searchTimeout = setTimeout(() => {
				this.customerTableOptions.page = 1;
				this.fetchCustomerData();
			}, 400);
		},

		onCustomerSearchClear() {
			if (this.searchTimeout) {
				clearTimeout(this.searchTimeout);
			}
			this.customerTableOptions.page = 1;
			this.fetchCustomerData();
		},

		onContactSearchInput() {
			// Debounced search
			if (this.searchTimeout) {
				clearTimeout(this.searchTimeout);
			}
			this.searchTimeout = setTimeout(() => {
				this.contactTableOptions.page = 1;
				this.fetchContactData();
			}, 400);
		},

		onContactSearchClear() {
			if (this.searchTimeout) {
				clearTimeout(this.searchTimeout);
			}
			this.contactTableOptions.page = 1;
			this.fetchContactData();
		},

		onFilterChange() {
			this.contactTableOptions.page = 1;
			this.fetchContactData();
		},

		// Map table column values to GraphQL sort field names for customers
		getCustomerSortFieldName(columnValue) {
			const sortFieldMap = {
				organization: "organization",
				salesRep: "salesRep.name",
				customerNumber: "customerNumber",
			};
			return sortFieldMap[columnValue] || "organization";
		},

		// Map table column values to GraphQL sort field names for contacts
		getContactSortFieldName(columnValue) {
			const sortFieldMap = {
				firstName: "firstName",
				lastName: "lastName",
				email: "email",
			};
			return sortFieldMap[columnValue] || "firstName";
		},

		// Build nested sort object from dot notation (e.g., "customerCategory.category")
		buildSortObject(fieldPath, direction) {
			const parts = fieldPath.split(".");
			if (parts.length === 1) {
				return { [parts[0]]: direction };
			}
			// Build nested object from inside out
			let result = { [parts[parts.length - 1]]: direction };
			for (let i = parts.length - 2; i >= 0; i--) {
				result = { [parts[i]]: result };
			}
			return result;
		},

		buildCustomerQueryVariables() {
			const { page, itemsPerPage, sortBy, sortDesc } = this.customerTableOptions;
			const keyword = (this.searchForm.keywords || "").trim();

			// Build where clause
			let where = null;
			if (keyword) {
				where = {
					or: [
						{ organization: { contains: keyword } },
						{ customerNumber: { contains: keyword } },
						{ salesRep: { name: { contains: keyword } } },
					],
				};
			}

			// Build dynamic sort order
			const sortColumn = sortBy[0] || "organization";
			const sortDirection = sortDesc[0] ? "DESC" : "ASC";
			const sortFieldPath = this.getCustomerSortFieldName(sortColumn);
			const order = [this.buildSortObject(sortFieldPath, sortDirection)];

			return {
				pageOffset: (page - 1) * itemsPerPage,
				take: itemsPerPage,
				order,
				where,
			};
		},

		buildContactQueryVariables() {
			const { page, itemsPerPage, sortBy, sortDesc } = this.contactTableOptions;
			const keyword = (this.searchForm.keywords || "").trim();

			// Build where clause
			let where = null;
			if (keyword) {
				where = {
					or: [
						{ firstName: { contains: keyword } },
						{ lastName: { contains: keyword } },
						{ cellPhoneNumber: { contains: keyword } },
						{ workPhoneNumber: { contains: keyword } },
						{ email: { contains: keyword } },
						{ customer: { customerNumber: { contains: keyword } } },
					],
				};
			}

			// Build dynamic sort order
			const sortColumn = sortBy[0] || "firstName";
			const sortDirection = sortDesc[0] ? "DESC" : "ASC";
			const sortFieldPath = this.getContactSortFieldName(sortColumn);
			const order = [this.buildSortObject(sortFieldPath, sortDirection)];

			return {
				pageOffset: (page - 1) * itemsPerPage,
				take: itemsPerPage,
				order,
				where,
			};
		},

		async fetchCustomerData() {
			if (this.$apollo.queries.customers) {
				await this.$apollo.queries.customers.refetch();
			}
		},

		async fetchContactData() {
			if (this.$apollo.queries.contacts) {
				await this.$apollo.queries.contacts.refetch();
			}
		},
	},
	async mounted() {
		await this.loadCustomerFromQueryParam();
	},
	beforeDestroy() {
		if (this.searchTimeout) {
			clearTimeout(this.searchTimeout);
		}
	},
	beforeRouteLeave(to, from, next) {
		this.$store.dispatch("globalState/saveCustomersState", {
			activeTab: this.activeTab,
			customerPage: this.customerTableOptions.page - 1,
			contactPage: this.contactTableOptions.page - 1,
			keywords: this.searchForm.keywords,
			showInactive: this.searchForm.inactive,
			customerSortBy: this.customerTableOptions.sortBy,
			customerSortDesc: this.customerTableOptions.sortDesc,
			contactSortBy: this.contactTableOptions.sortBy,
			contactSortDesc: this.contactTableOptions.sortDesc,
			scrollPosition: window.scrollY,
		});
		next();
	},
};
</script>

<style lang="scss" scoped>
.customer-page {
	height: calc(100vh - 88px);
	display: flex;
	flex-direction: column;
	overflow: hidden;
}

.customer-table-container {
	display: flex;
	flex-direction: column;
	flex: 1;
	min-height: 0;
	overflow: hidden;
}

.customer-tab-card {
	border-radius: 4px 4px 0 0 !important;
	border-bottom: 1px solid $color-gray;
	flex-shrink: 0;

	.customer-header-tabs {
		.v-btn {
			color: $color-black;
			background-color: $color-gray-2;
			text-transform: initial;

			&.active {
				color: $color-white;
				background-color: $color-tertiary;
			}

			::v-deep {
				i {
					font-size: 2rem;
					margin-right: 0.8rem;
				}
			}
		}
	}
}

.customer-toolbar-card {
	border-radius: 0 !important;
	border-bottom: 1px solid $color-gray;
	flex-shrink: 0;
}

.customer-search {
	::v-deep .v-input__slot {
		background-color: $color-gray-light-2 !important;
	}
}

.customer-data-table {
	border-radius: 0 0 4px 4px !important;
	display: flex;
	flex-direction: column;
	flex: 1;
	min-height: 0;
	overflow: hidden;

	::v-deep .v-data-table__wrapper {
		flex: 1;
		overflow-y: auto;
		overflow-x: auto;
		table {
			min-width: 600px;
		}
	}

	::v-deep .v-data-footer {
		flex-shrink: 0;
	}

	::v-deep tbody tr {
		cursor: pointer;
		transition: background-color 0.2s;

		&:hover {
			background-color: $color-gray-light !important;
		}
	}

	::v-deep > .v-data-table__wrapper > table > thead > tr > th {
		font-size: 1.4rem !important;
		font-weight: 500 !important;
		color: #000 !important;
		text-transform: uppercase;
		background-color: $color-gray-light-2 !important;
		white-space: nowrap;
	}

	::v-deep > .v-data-table__wrapper > table > tbody > tr > td {
		height: 60px !important;
		font-size: 1.6rem !important;
	}

	::v-deep .v-data-footer {
		font-size: 1.25rem !important;
	}

	::v-deep .v-data-footer__select .v-select__selections .v-select__selection--comma {
		font-size: 1.15rem !important;
	}
}
</style>
